import React, { ElementType, forwardRef, useEffect, useMemo, useState, useRef } from 'react';
import { Box, BoxProps, Button, ButtonProps, Slider, SliderProps } from '@mui/material';
import { useCarousel } from './hooks';
import type { Swiper as CarouselInstance } from 'swiper';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';
import { SliderContainer } from '@/src/materialui/slider/SliderContainer';

export interface CarouselPaginationVariantProps extends Omit<BoxProps<ElementType>, 'children' | 'position'> {
  /**
   * Carousel instance from useCarousel hook
   */
  carousel: CarouselInstance;
}
export interface CarouselPaginationDotsProps extends CarouselPaginationVariantProps {
  variant?: 'dots';
  /**
   * Properties applied to wrapper of dots
   */
  dotsWrapperProps?: BoxProps<ElementType>;
  /**
   * Properties applied to each dot
   */
  dotProps?: BoxProps<ElementType>;
}
export interface CarouselPaginationFractionProps extends CarouselPaginationVariantProps {
  variant?: 'fraction';
  /**
   * Properties applied to button used to render text
   */
  buttonProps?: ButtonProps;
}
export interface CarouselPaginationLinesProps extends CarouselPaginationVariantProps {
  variant?: 'lines';
  /**
   * Properties applied to each line
   */
  linesProps?: ButtonProps;
  /**
   * Show progress in active line instead
   */
  lineProgress?: number;
  /**
   * Properties applied to active line progress
   */
  lineProgressProps?: BoxProps<ElementType>;
}
export interface CarouselPaginationSliderProps extends CarouselPaginationVariantProps {
  variant?: 'slider';
  /**
   * Properties applied to Slider
   */
  sliderProps?: SliderProps;
}
export interface CarouselPaginationScrollbarProps extends CarouselPaginationVariantProps {
  variant?: 'scrollbar';
  /**
   * Properties applied to Slider
   */
  sliderProps?: SliderProps;
}
export interface CarouselPaginationBaseProps {
  /**
   * Optional Carousel instance from useCarouselInstance hook
   */
  carousel?: CarouselInstance;
  /**
   * Append style to wrapper box to change position of control
   * @default outside
   */
  position?: 'outside' | 'inside';
}
export type CarouselPaginationProps = CarouselPaginationBaseProps &
  (
    | Omit<CarouselPaginationDotsProps, 'carousel'>
    | Omit<CarouselPaginationFractionProps, 'carousel'>
    | Omit<CarouselPaginationLinesProps, 'carousel'>
    | Omit<CarouselPaginationSliderProps, 'carousel'>
    | Omit<CarouselPaginationScrollbarProps, 'carousel'>
  );

/**
 * Pagination controls
 */
export const CarouselPagination: React.FC<CarouselPaginationProps> = forwardRef(
  ({ carousel: carouselProp, position, variant, sx, ...rest }, ref) => {
    const carouselHook = useCarousel();
    const carousel = carouselProp || carouselHook;
    // decide on component used
    const VariantComponent = useMemo(() => {
      switch (variant) {
        case 'scrollbar':
          return ScrollbarVariant;
        case 'slider':
          return SliderVariant;
        case 'fraction':
          return FractionVariant;
        case 'lines':
          return LinesVariant;
        case 'dots':
          return DotsVariant;
        default:
          return DotsVariant;
      }
    }, [variant]);

    return (
      <VariantComponent
        carousel={carousel}
        sx={{
          ...(position === 'inside' && {
            position: 'absolute',
            zIndex: '90',
            bottom: '0',
            left: '0',
            right: '0',
          }),
          ...sx,
        }}
        ref={ref}
        position={position}
        {...rest}
      />
    );
  },
);

/**
 * Dots variant (...)
 */
export const DotsVariant: React.FC<CarouselPaginationDotsProps> = forwardRef(
  ({ carousel, dotsWrapperProps, dotProps, position, sx, ...rest }, ref) => {
    const { sx: dotsWrapperSx, ...restDotsWrapper } = dotsWrapperProps ?? {};
    const { sx: dotSx, ...restDot } = dotProps ?? {};

    return (
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...sx,
        }}
        ref={ref}
        {...rest}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '1000px',
            py: 0.5,
            px: 1,
            gap: 0.5,
            // ensure width does not change during transition
            //  dots width space + gap space + extra active dot space + padding
            width: `${(carousel?.slides.length || 0) * 12 + ((carousel?.slides.length || 1) - 1) * 4 + 4 + 16}px`,
            // on image variant from Figma
            ...(position === 'inside' && {
              backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'surface.primary' : 'transparent.backdrop'),
            }),
            ...dotsWrapperSx,
          }}
          {...restDotsWrapper}
        >
          {[...new Array(carousel?.slides.length || 0)].map((_, idx) => (
            <Box
              key={idx}
              className={
                !carousel?.params?.loop && carousel?.isEnd && idx === (carousel?.slides.length || 0) - 1
                  ? 'active'
                  : (!carousel?.isEnd || carousel.params?.loop) && idx === carousel?.realIndex
                    ? 'active'
                    : undefined
              }
              sx={{
                p: '0',
                width: '12px',
                height: '12px',
                // scale to 8px
                transform: 'scale(0.6667)',
                borderRadius: '100%',
                backgroundColor: 'onSurface.os500',
                transition: 'transform 0.3s ease-out, background-color 0.3s ease-out, margin 0.3s ease-out',
                '&.active': {
                  transform: 'scale(1)',
                  backgroundColor: 'text.primary',
                  margin: '0 2px',
                },
                ...dotSx,
              }}
              {...restDot}
            />
          ))}
        </Box>
      </Box>
    );
  },
);

/**
 * Fraction variant (text 1/3)
 */
export const FractionVariant: React.FC<CarouselPaginationFractionProps> = forwardRef(
  ({ carousel, buttonProps, position, sx, ...rest }, ref) => {
    const { sx: buttonSx, ...restButton } = buttonProps ?? {};

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // space is needed for focus overflow
          p: 2,
          ...sx,
        }}
        ref={ref}
        {...rest}
      >
        <Button
          variant="text"
          aria-controls="carousel"
          sx={{
            px: 2,
            py: 0.25,
            borderRadius: '4px',
            // custom typography according to Figma (Pagination/Compact)
            fontSize: '18px',
            lineHeight: '28px',
            letterSpacing: '0.02em',
            ...getInteractiveStyle(
              {
                backgroundColor: 'transparent.backdrop',
              },
              {
                backgroundColor: 'onSurface.os100',
              },
            ),
            ...buttonSx,
          }}
          onClick={() =>
            // at the end, slide to beginning
            carousel?.isEnd && !carousel.params?.loop ? carousel?.slideTo(0) : carousel?.slideNext()
          }
          {...restButton}
        >
          <Box component="span">
            <Box component="span" sx={{ fontWeight: '700', color: 'onSurface.os900' }}>
              {`${(carousel?.realIndex || 0) + 1}`}
            </Box>
            <Box component="span" sx={{ color: 'onSurface.os700', fontWeight: '400', mx: 0.5 }}>
              {' / '}
            </Box>
            <Box component="span" sx={{ color: 'onSurface.os700', fontWeight: '300' }}>
              {`${carousel?.slides.length || 0}`}
            </Box>
          </Box>
        </Button>
      </Box>
    );
  },
);

/**
 * Lines variant (- - -)
 */
export const LinesVariant: React.FC<CarouselPaginationLinesProps> = forwardRef(
  ({ carousel, sx, linesProps, lineProgress, lineProgressProps, position, ...rest }, ref) => {
    const { sx: lineSx, ...lineRest } = linesProps ?? {};
    const { sx: lineProgressSx, ...lineProgressRest } = lineProgressProps ?? {};

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'stretch', md: 'center' },
          flexGrow: { xs: '1', md: '0' },
          gap: 1,
          // space is needed for focus overflow
          p: 2,
          ...sx,
        }}
        ref={ref}
        {...rest}
      >
        {[...new Array(carousel?.slides.length || 0)].map((_, idx) => {
          const active =
            (!carousel?.params?.loop && carousel?.isEnd && idx === (carousel?.slides.length || 0) - 1) ||
            ((!carousel?.isEnd || carousel.params?.loop) && idx === carousel?.realIndex);
          const seen = idx <= (carousel?.realIndex || 0);

          return (
            <Button
              key={idx}
              color="inherit"
              className={active ? 'active' : undefined}
              sx={{
                p: 0,
                minHeight: '0',
                height: active || seen ? '8px' : '4px',
                borderRadius: active || seen ? '4px' : '2px',
                width: { xs: 'auto', md: '56px' },
                flexGrow: { xs: '1', md: '0' },
                justifyContent: 'flex-start',
                ...getInteractiveStyle(
                  {
                    backgroundColor: 'surface.primary',
                    '& .progress-line': {
                      backgroundColor: 'brand.secondary',
                    },
                  },
                  {
                    backgroundColor: 'surface.secondary',
                    '& .progress-line': {
                      backgroundColor: 'brandHover.secondary',
                    },
                  },
                ),
                ...lineSx,
              }}
              aria-controls="carousel"
              onClick={() => carousel?.[carousel.params?.loop ? 'slideToLoop' : 'slideTo'](idx)}
              {...lineRest}
            >
              <Box
                sx={{
                  // invisible hitbox expanding clickable area
                  position: 'absolute',
                  top: '50%',
                  left: '0',
                  right: '0',
                  height: '24px', // Minimum a11y click area
                  transform: 'translateY(-50%)',
                  pointerEvents: 'auto',
                }}
              />
              <Box sx={{ borderRadius: '4px', height: '100%', width: '100%', overflow: 'hidden' }}>
                <Box
                  className="progress-line"
                  role={active ? 'progressbar' : undefined} // Only the active one gets a role
                  aria-valuenow={active ? lineProgress : undefined}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  sx={{
                    opacity: lineProgress !== undefined || seen ? '1' : '0',
                    width: '100%',
                    backgroundColor: 'brand.secondary',
                    height: '100%',
                    transformOrigin: 'left',
                    transform: `scaleX(${lineProgress !== undefined && active ? lineProgress / 100 : seen ? 1 : 0})`,
                    transition: `${lineProgress !== undefined ? 'transform 300ms ease-in-out, ' : ''}opacity 300ms ease-out, background-color 300ms ease-out`,
                    ...lineProgressSx,
                  }}
                  {...lineProgressRest}
                />
              </Box>
            </Button>
          );
        })}
      </Box>
    );
  },
);

/**
 * Slider variant (___)
 */
export const SliderVariant: React.FC<CarouselPaginationSliderProps> = forwardRef(
  ({ carousel, sliderProps, sx, progressProps: __, position, ...rest }, ref) => {
    const { sx: sliderSx, ...restSlider } = sliderProps ?? {};
    // slider current value that might not match carousel progress
    const [value, setValue] = useState<number>(Math.round((carousel?.progress || 0) * 100));
    // watch user manipulating slider
    const dragging = useRef<boolean>(false);
    const [, setRerender] = useState({});
    // monitor carousel progress to update slider value
    useEffect(() => {
      if (!dragging.current) {
        setValue(Math.round((carousel?.progress || 0) * 100));
      }
    }, [carousel?.progress]);
    // extra monitor of states
    useEffect(() => {
      if (carousel) {
        const handleMove = () => {
          if (!dragging.current) {
            setRerender({});
          }
        };

        // monitor of movement to immediately move thumb
        carousel.on('sliderMove', handleMove);

        return () => {
          carousel.off('sliderMove', handleMove);
        };
      }
    }, [carousel]);

    return (
      <Box sx={{ my: 2, ...sx }} ref={ref} {...rest}>
        <Slider
          value={value}
          min={0}
          max={100}
          onChange={(_, newValue: number) => {
            // prevent value updates from carousel progress
            dragging.current = true;
            // smoothly move carousel
            carousel?.setProgress(newValue / 100, 150);
            // update slider value
            setValue(newValue);
          }}
          onChangeCommitted={() => {
            // make sure to slide to correct position after user stop touching slider
            carousel?.slideToClosest(300);
            // enable changing value from carousel progress
            dragging.current = false;
          }}
          sx={{
            display: 'block',
            // from 6px to 4px
            height: '4px',
            // keep 32px height
            py: '14px',
            // restyle to less prominent variant
            '.MuiSlider-rail': {
              color: 'surface.primary',
            },
            '.MuiSlider-track': {
              color: 'onSurface.os500',
              // match transition speed of carousel
              transitionDuration: `${carousel?.params?.speed ?? 300}ms`,
            },
            // hide control thumb
            '.MuiSlider-thumb': {
              display: 'none',
            },
            ...sliderSx,
          }}
          aria-controls="carousel"
          {...restSlider}
        />
      </Box>
    );
  },
);

/**
 * Scrollbar variant (_--)
 */
export const ScrollbarVariant: React.FC<CarouselPaginationSliderProps> = forwardRef(
  ({ carousel, sliderProps, position, ...rest }, ref) => {
    const { sx: sliderSx, ...restSlider } = sliderProps ?? {};
    // watch states
    const [carouselDragging, setCarouselDragging] = useState<boolean>(false);
    const [, setRerender] = useState({});
    // extra monitor of states
    useEffect(() => {
      if (carousel) {
        const handleDragStart = () => setCarouselDragging(true);
        const handleDragEnd = () => setCarouselDragging(false);
        const handleResize = () => setRerender({});

        // monitor of dragging to remove thumb transition for immediate feedback
        carousel.on('touchStart', handleDragStart);
        carousel.on('touchEnd', handleDragEnd);
        // monitor resize to change size of the thumb
        carousel.on('resize', handleResize);

        return () => {
          carousel.off('touchStart', handleDragStart);
          carousel.off('touchEnd', handleDragEnd);
          carousel.off('resize', handleResize);
        };
      }
    }, [carousel]);
    // calculate size of the thumb based on the visible content and full content
    const thumbSize = useMemo<number>(() => {
      if (carousel?.wrapperEl?.scrollWidth && carousel?.width) {
        const size = Math.ceil(carousel?.width ** 2 / carousel?.wrapperEl?.scrollWidth);
        return Math.max(size, 40);
      }
      return 40;
    }, [carousel?.wrapperEl?.scrollWidth, carousel?.width]);

    return (
      <SliderVariant
        ref={ref}
        carousel={carousel}
        component={SliderContainer}
        thumbSize={thumbSize}
        sliderProps={{
          // prevent selection via keyboard, Carousel can be already controlled with Keyboard, and we are not showing focus ring
          tabIndex: -1,
          // remove valueLabel tooltip above thumb
          valueLabelDisplay: 'off',
          sx: {
            // restyle to match scrollbar
            '.MuiSlider-rail, .MuiSlider-track': {
              color: 'surface.primary',
            },
            '.MuiSlider-thumb': {
              display: 'flex',
              width: `${thumbSize}px`,
              maxWidth: 'none',
              height: '4px',
              border: 'none',
              backgroundColor: 'onSurface.os500',
              // hitbox target
              '&:after': {
                width: `${thumbSize}px`,
              },
              // match transition speed of carousel
              ...(!carouselDragging && {
                transition: `left ${carousel?.params?.speed ?? 300}ms ease-out`,
              }),
              // remove focus style
              '&.Mui-focusVisible': {
                boxShadow: 'none',
                '&:before': {
                  content: 'none',
                },
              },
            },
            ...sliderSx,
          },
          ...restSlider,
        }}
        {...rest}
      />
    );
  },
);
